import { useState, useEffect } from 'react';
import { useStyles } from './styles';
import { useStore } from 'hooks/useRootStore';
import { useQuery, useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { observer } from 'mobx-react-lite';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from '@material-ui/core';
import { Link } from 'react-router-dom';
// import { LeftTabsBlock } from 'components/DashBoardBlocks';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import LockIcon from '@mui/icons-material/Lock';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { makeStyles } from '@material-ui/core/styles';
import GoogleSvg from '../../../assets/googlelogo.svg';
import ZoomSvg from '../../../assets/zoom-logo.png';
import MicrosoftSvg from '../../../assets/microsoft.png';
import GithubSvg from '../../../assets/GitHub-logo.png';

const APP_LOGOS: Record<string, string> = {
  google: GoogleSvg,
  zoom: ZoomSvg,
  microsoft: MicrosoftSvg,
  github: GithubSvg,
};

const useCardStyles = makeStyles({
  grid: { marginBottom: 32 },
  card: {
    position: 'relative',
    borderRadius: '16px',
    padding: '8px',
    height: '160px',
    transition: '0.25s',
    border: '1px solid #e5e7eb',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    borderTop: '4px solid #9ca3af',
    cursor: 'default',
    opacity: 0.75,
  },
  cardConnected: {
    borderTop: '4px solid #22c55e',
    opacity: 1,
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
    },
  },
  cardPending: {
    borderTop: '4px solid #f59e0b',
    opacity: 0.9,
    cursor: 'not-allowed',
  },
  cardAvailable: {
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    },
  },
  // badgeArea: {
  //     position: 'absolute',
  //     top: 10,
  //     right: 10,
  //     display: 'flex',
  //     gap: '4px',
  //     alignItems: 'center',
  // },
  badgeArea: {
    position: 'absolute',
    top: 10,
    right: 10,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 80,
    flexWrap: 'nowrap', // wrap mat hone do
  },
  badgeLeft: {
    position: 'absolute',
    top: 10,
    left: 10,
  },

  badgeRight: {
    position: 'absolute',
    top: 10,
    right: 10,
  },

  checkIcon: { color: '#22c55e', fontSize: '22px !important' },
  lockIcon: { color: '#22c55e', fontSize: '22px !important' },
  pendingIcon: { color: '#f59e0b', fontSize: '22px !important' },
  removeIcon: { color: '#9ca3af', fontSize: '22px !important' },
  appRow: { display: 'flex', alignItems: 'center', gap: '14px', marginTop: '28px' },
  appIcon: { width: '60px', height: '60px', objectFit: 'contain' },
  appInitial: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    background: '#eef2ff',
    color: '#3730a3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 700,
  },
  appName: { fontWeight: 700, fontSize: '20px', color: '#111827' },
  appDesc: { color: '#6b7280', fontSize: '14px', marginTop: '4px' },
  sectionHeading: {
    fontWeight: 600,
    fontSize: '18px',
    color: '#111827',
    margin: '24px 0 12px',
    paddingLeft: '4px',
  },
});

export const EndUserProfile = observer(() => {
  const classes = useStyles();
  const c = useCardStyles();
  const {
    dashboardStore: { getDashboard },
    AppStore: { getConnectedApps, requestAppAccess },
    notificationStore: { openNotification },
  } = useStore();

  // track which providers have a pending request (sent this session)
  const [pendingProviders, setPendingProviders] = useState<Set<string>>(new Set());
  // dialog state
  const [confirmApp, setConfirmApp] = useState<{ provider: string; app_name: string } | null>(null);

  const getDashboardReq = useQuery('getDashboardReq', () => getDashboard(), {
    onError: () => openNotification('error', `Can't get dashboard data`),
  });

  const getConnectedAppsReq = useQuery('getConnectedAppsReq', () => getConnectedApps(), {
    onError: () => openNotification('error', `Can't get apps`),
  });

  const requestMutation = useMutation((provider: string) => requestAppAccess(provider), {
    onSuccess: (_data, provider) => {
      setPendingProviders((prev) => new Set(prev).add(provider));
      openNotification('success', 'Access request sent successfully.');
    },
    onError: () => openNotification('error', 'Failed to send access request.'),
  });

  const data = getDashboardReq?.data?.data;
  const lastlocation = data?.last_location;
  const apps: any[] = getConnectedAppsReq?.data?.data || [];
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userDataFromUrl = urlParams.get('userData');
    const tokenFromUrl = urlParams.get('token');
    if (tokenFromUrl) {
      localStorage.setItem('token', `Bearer ${tokenFromUrl}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (userDataFromUrl) {
      try {
        const parsedUserData = JSON.parse(decodeURIComponent(userDataFromUrl));
        localStorage.setItem('Profile', JSON.stringify(parsedUserData));
        // setData(parsedUserData);
      } catch (error) {
        localStorage.setItem('profile', userDataFromUrl);
        // setData(userDataFromUrl);
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAppPreloader(getDashboardReq.isFetching || getConnectedAppsReq.isFetching || requestMutation.isLoading);

  const getCardClass = (app: any) => {
    const provider = app.provider?.toLowerCase();
    if (app.is_connected) return `${c.card} ${c.cardConnected}`;
    if (pendingProviders.has(provider)) return `${c.card} ${c.cardPending}`;
    return `${c.card} ${c.cardAvailable}`;
  };

  const getStatusLabel = (app: any) => {
    const provider = app.provider?.toLowerCase();
    if (app.is_connected) return app.login_url ? 'Access granted' : 'Connected';
    if (pendingProviders.has(provider)) return 'Request pending…';
    return 'Available';
  };

  const handleCardClick = (app: any) => {
    if (app.is_connected && app.login_url) {
      window.open(app.login_url, '_blank');
      return;
    }
    if (app.is_connected) return; // connected but no login_url — nothing to do
    // const provider = app.provider?.toLowerCase();
    // if (pendingProviders.has(provider)) return; // already requested
    // setConfirmApp({ provider, app_name: app.app_name });
  };

  const handleConfirm = () => {
    if (!confirmApp) return;
    requestMutation.mutate(confirmApp.provider);
    setConfirmApp(null);
  };

  return (
    <>
      <Container maxWidth='xl' className={classes.container}>
        {/* Stat Cards */}
        <Grid className={classes.infoContainer} container alignItems='stretch' spacing={3}>
          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{data?.total_departments || '0'}</p>
              <p className={classes.title}>
                Number of <br /> Departments
              </p>
              <Link to={'/'} className={classes.link}></Link>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{data?.total_groups || '0'}</p>
              <p className={classes.title}>
                Number of <br /> Groups
              </p>
              <Link to={'/'} className={classes.link}></Link>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{data?.total_apps || '0'}</p>
              <p className={classes.title}>
                Number of <br /> Apps
              </p>
              <Link to={'/'} className={classes.link}></Link>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value} style={{ fontSize: 19 }}>
                {data?.last_login
                  ? new Date(data.last_login).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })
                  : '-'}
              </p>
              <p className={classes.title}>
                Last
                <br />
                Login
              </p>
            </Paper>
            <Link to={'/'} className={classes.link}></Link>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value} style={{ fontSize: 22 }}>
                {lastlocation?.city || '-'}
              </p>
              <p className={classes.title}>
                Last
                <br />
                Location
              </p>
              <Link to={'/'} className={classes.link}></Link>
            </Paper>
          </Grid>
        </Grid>

        {/* Apps Section */}
        {!getConnectedAppsReq.isFetching && (
          <>
            <p className={c.sectionHeading}>Apps</p>
            {apps.length > 0 ? (
              <Grid container spacing={3} className={c.grid}>
                {apps.map((app: any, index: number) => {
                  const provider = app.provider?.toLowerCase();
                  const logo = APP_LOGOS[provider];
                  const isConnected = app.is_connected;
                  const hasOidc = isConnected && !!app.login_url;
                  const isPending = !isConnected && pendingProviders.has(provider);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Tooltip title={!isConnected && !isPending ? 'Available' : ''} placement='top'>
                        <Card className={getCardClass(app)} onClick={() => handleCardClick(app)}>
                          <CardContent>
                            {/* Left Corner */}
                            <div className={c.badgeLeft}>
                              {hasOidc && (
                                <Tooltip title='SSO / OIDC login available'>
                                  <LockIcon className={c.lockIcon} />
                                </Tooltip>
                              )}
                            </div>

                            {/* Right Corner */}
                            <div className={c.badgeRight}>
                              {isConnected ? (
                                <CheckCircleIcon className={c.checkIcon} />
                              ) : isPending ? (
                                <Tooltip title='Access request pending approval'>
                                  <HourglassEmptyIcon className={c.pendingIcon} />
                                </Tooltip>
                              ) : (
                                <RemoveCircleIcon className={c.removeIcon} />
                              )}
                            </div>
                            <div className={c.appRow}>
                              {logo ? (
                                <img src={logo} alt={provider} className={c.appIcon} />
                              ) : (
                                <div className={c.appInitial}>
                                  {(app.app_name || app.provider || 'A').charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div>
                                <Typography className={c.appName}>{app.app_name || app.provider}</Typography>
                                <Typography className={c.appDesc}>{getStatusLabel(app)}</Typography>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Tooltip>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Typography style={{ fontSize: '15px', color: '#6b7280', marginBottom: 24 }}>
                No apps available.
              </Typography>
            )}
          </>
        )}
      </Container>

      {/* Confirm Request Dialog */}
      <Dialog open={!!confirmApp} onClose={() => setConfirmApp(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Request App Access</DialogTitle>
        <DialogContent>
          <Typography>
            Send an access request for <strong>{confirmApp?.app_name}</strong> to the app manager?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApp(null)} color='default'>
            Cancel
          </Button>
          <Button onClick={handleConfirm} color='primary' variant='contained'>
            Yes, Send Request
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
